import { CommonModule, NgFor, NgIf } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxChange, MatCheckboxModule } from '@angular/material/checkbox';
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ActivatedRoute, Router } from '@angular/router';
import { UntilDestroy } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import { intersection } from 'lodash';
import { BehaviorSubject, Observable, combineLatest, shareReplay } from 'rxjs';
import { distinctUntilChanged, filter, map, switchMap, take, tap } from 'rxjs/operators';

import { DynamicTicketTypeService } from '@app/services/dynamic-ticket-type.service';
import { PropertiesService } from '@app/services/properties.service';
import { PuSubscribable } from '@app/utils/pu-subscribe';
import {
  Board,
  BoardColumn,
  BoardConfiguration,
  BoardLayoutTypeList,
  BoardOutputBehaviourList,
  BoardOutputTypeList,
  BoardSourceTypeList,
  BoardTypeList,
  DONT_CREATE_TICKETS,
  DynamicTicketTypeDefault,
  PoNumberIsAvailableRule,
  ProgressTypeList,
  ProjectBoardOutputTypeList,
  RestBoardModel,
  SendBackBehaviourList,
  TurnTransitBackByTurnFlowBehaviorList,
  disallowSourceTypeOutputTypeMap,
} from '@main-application/boards/interfaces/board';
import { AddProjectTypeDialogComponent } from '@main-application/management/pages/service-configuration/project-types/components/add-project-type-dialog/add-project-type-dialog.component';
import {
  clearCreatedNewProject,
  clearProjectTypes,
  createProjectType,
  loadProjectTypes,
} from '@main-application/management/pages/service-configuration/store/service-configuration.actions';
import {
  selectCreatedNewProject,
  selectProjectTypes,
} from '@main-application/management/pages/service-configuration/store/service-configuration.selectors';
import { selectUserPermissionLevel } from '@main-application/store/selectors/user.selectors';
import { selectPortfolios } from '@portfolio/store/portfolio.selectors';
import { AppRoutes } from '@shared/constants/app-routes.const';
import { BooleanListConst } from '@shared/constants/boolean-list.const';
import { ButtonType } from '@shared/enums/button-type';
import { EIcon } from '@shared/enums/icon.enum';
import { PermissionLevelType } from '@shared/enums/permission-level.enum';
import { checkPermissionLevel } from '@shared/functions/check-permission-level.function';
import { IRadioButtonOption } from '@shared/interfaces/radio-button-option.interface';
import { RestPortfolioModel } from '@shared/interfaces/rest-portfolio-model.interface';
import { EnumerationValuePipe } from '@shared/pipes/enumeration-value.pipe';
import { isControlInvalid } from '@shared/utils/forms/is-control-invalid';
import { filterNullish$ } from '@shared/utils/rxjs/filter-nullish.rxjs.util';
import { SnackbarService } from '@ui-components/components/customized-snackbar/snackbar.service';
import { IconComponent } from '@ui-components/components/icon/icon.component';
import { CustomColorPickerComponent } from '@ui-components/controls/custom-color-picker/custom-color-picker.component';
import { InputModule } from '@ui-components/controls/input/input.module';
import { RadioButtonDropdownModule } from '@ui-components/controls/radio-button-dropdown/radio-button-dropdown.module';

import { updateFormForBoardType } from './boards-create';
import { allPortfoliosValue } from '../../application-menu/property-selector/constants';
import {
  BoardLayoutType,
  BoardOutputBehaviour,
  BoardOutputType,
  BoardProgressType,
  BoardSourceType,
  BoardType,
  SendBackBehaviour,
  TurnTransitBackByTurnFlowBehavior,
} from '../interfaces/board.enums';
import { IBoardGroup } from '../interfaces/borad-groups.interface';
import { BoardEventService } from '../services/board-event.service';
import { BoardService } from '../services/board.service';

@UntilDestroy()
@Component({
  selector: 'app-boards-create',
  templateUrl: './boards-create.component.html',
  styleUrls: ['./boards-create.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    MatButtonModule,
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    RadioButtonDropdownModule,
    InputModule,
    CustomColorPickerComponent,
    IconComponent,
    MatProgressSpinnerModule,
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    NgIf,
    NgFor,
    MatCheckboxModule,
  ],
})
export class BoardsCreateComponent extends PuSubscribable implements OnInit {
  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private dialogRef: MatDialogRef<BoardsCreateComponent, BoardConfiguration>,
    private dialog: MatDialog,
    private formBuilder: FormBuilder,
    private boardService: BoardService,
    private boardEventService: BoardEventService,
    private propertiesService: PropertiesService,
    private ticketTypeService: DynamicTicketTypeService,
    private store: Store<{}>,
    private cdr: ChangeDetectorRef,
    private snackbarService: SnackbarService,
    private enumerationValuePipe: EnumerationValuePipe,
    @Inject(MAT_DIALOG_DATA) public data?: BoardConfiguration & { columns: BoardColumn[] }
  ) {
    super();
  }

  private boardActionSubject = new BehaviorSubject(false);
  inProgress$ = this.boardActionSubject.asObservable();
  ButtonType = ButtonType;
  colOptions: IRadioButtonOption<string>[] =
    (this.data?.columns ?? []).map(col => ({
      value: col.id,
      label: col.name,
    })) || [];
  form = this.initFormGroups();
  private lastAllowedBoardLayoutType: BoardLayoutType = this.form.get('boardLayoutType')?.value;
  boardGroupForm = this.initBoardGroupConfig();
  groupsArray: FormArray = this.boardGroupForm.get('groups') as FormArray;
  progressTypeList = ProgressTypeList;
  boardSourceTypeList = BoardSourceTypeList;
  boardTypeList = BoardTypeList;
  boardLayoutTypeList = BoardLayoutTypeList;
  boardOutputTypeList = BoardOutputTypeList;
  sendBackBehaviourList = SendBackBehaviourList;
  turnTransitBackByTurnFlowBehaviorList = TurnTransitBackByTurnFlowBehaviorList;
  projectBoardOutputTypeList = ProjectBoardOutputTypeList;
  boardOutputBehaviorList = BoardOutputBehaviourList;
  dynamicTicketTypeList = DynamicTicketTypeDefault;
  pmNumberBehaviorList = PoNumberIsAvailableRule;
  booleanListConst = BooleanListConst;
  permissionList: PermissionLevelType[] = [];
  userPortfolios: IRadioButtonOption<number>[] = [];
  propertyList: IRadioButtonOption<number>[] = [];
  isEditMode: boolean;
  isSettingsOpened = false;
  scrollToBottom$ = new BehaviorSubject<any>(null);
  private suppressCustomExpand = false;
  private allProperties: IRadioButtonOption<number>[] = [];
  projectTypes$: Observable<IRadioButtonOption<number>[]> = this.store.select(selectProjectTypes).pipe(
    filterNullish$(),
    map(data => data.map(el => ({ label: el.name, value: el.id }))),
    shareReplay({ bufferSize: 1, refCount: true })
  );
  protected readonly BoardLayoutType = BoardLayoutType;
  protected readonly BoardOutputType = BoardOutputType;

  ngOnInit(): void {
    this.isEditMode = !!this.data?.id;
    this.store.dispatch(clearProjectTypes());
    this.store.dispatch(loadProjectTypes());

    this.getDynamicTicketTypes();

    this.form.patchValue({
      ...this.data,
      defaultScheduleDynamicTicketTypeId: this.form.value.defaultScheduleDynamicTicketTypeId,
      defaultCostDynamicTicketTypeId: this.form.value.defaultCostDynamicTicketTypeId,
    });
    this.store
      .select(selectPortfolios)
      .pipe(
        filter(portfolios => !!portfolios?.length),
        tap((portfolios: RestPortfolioModel[]) => {
          this.userPortfolios = [];
          this.userPortfolios.push(
            ...portfolios.map<IRadioButtonOption<number>>(item => {
              return {
                value: item.id,
                label: item.name,
              };
            })
          );
          if (this.data?.allPortfolios === true || this.data === null || this.data === undefined) {
            this.form.controls.selectedPortfolioIds.setValue([...this.userPortfolios.map(p => p.value)]);
          }
          this.cdr.markForCheck();
        })
      )
      .subscribe()
      .untilDestroyed(this);

    this.form.controls.selectedPortfolioIds.valueChanges
      .pipe(
        distinctUntilChanged(),
        tap(() => {
          this.updatePropertyList();
          this.cdr.markForCheck();
        })
      )
      .subscribe()
      .untilDestroyed(this);

    this.store
      .select(selectUserPermissionLevel)
      .subscribe(permissionList => {
        this.permissionList = permissionList ?? [];
        if (
          !this.canAccessProjectBoards() &&
          this.form.get('boardLayoutType')?.value === BoardLayoutType.ProjectsBoard
        ) {
          this.handleBoardLayoutTypeChange(BoardLayoutType.ProjectsBoard, false);
        }
      })
      .untilDestroyed(this);

    this.form.controls.boardType.valueChanges
      .pipe(
        distinctUntilChanged(),
        tap(value => {
          const isProjectLayoutType = this.form.get('boardLayoutType')?.value === BoardLayoutType.ProjectsBoard;
          if (value !== BoardType.Custom && isProjectLayoutType) {
            this.form.get('boardLayoutType').setValue(BoardLayoutType.TurnoverBoard);
          }

          const selected = this.boardTypeList.flat().find(x => x.value === value);
          if (selected?.label) {
            this.form.controls.name.setValue(selected.label);
          }
          updateFormForBoardType(value, this.form);
          if (value === BoardType.Custom && !this.suppressCustomExpand) {
            this.isSettingsOpened = true;
          }

          switch (value) {
            case BoardType.MakeReady:
            case BoardType.Reno:
              this.form.controls.outputAction.setValidators(Validators.required);
              this.form.controls.outputAction.updateValueAndValidity();
              break;
            case BoardType.Marketing:
              this.form.controls.outputAction.clearValidators();
              this.form.controls.outputAction.updateValueAndValidity();
              break;
            case BoardType.Custom:
              this.form.controls.outputAction.clearValidators();
              this.form.controls.outputAction.updateValueAndValidity();
              break;
          }

          this.cdr.markForCheck();
        })
      )
      .subscribe()
      .untilDestroyed(this);

    if (
      !this.isEditMode &&
      !this.form.controls.boardType.value &&
      this.form.get('boardLayoutType')?.value === BoardLayoutType.TurnoverBoard
    ) {
      this.form.controls.boardType.setValue(BoardType.MakeReady);
    }

    this.propertiesService
      .getAllUserProperties()
      .pipe(
        filter(properties => !!properties?.length),
        tap(properties => {
          this.allProperties = properties.map(item => ({
            value: item.id,
            label: item.name,
          }));
          this.updatePropertyList();
          this.cdr.markForCheck();
        })
      )
      .subscribe()
      .untilDestroyed(this);

    this.form
      .get('boardLayoutType')
      ?.valueChanges.pipe(distinctUntilChanged())
      .subscribe(layoutType => {
        this.handleBoardLayoutTypeChange(layoutType);
      })
      .untilDestroyed(this);

    if (!this.isEditMode) {
      this.handleBoardLayoutTypeChange(this.form.get('boardLayoutType')?.value, false);
    }

    this.store
      .select(selectCreatedNewProject)
      .pipe(filter(projectType => !!projectType))
      .subscribe(projectType => {
        const projectTypes = this.form.get('projectTypes')?.value || [];
        projectTypes.push(projectType.id);
        this.form.get('projectTypes').setValue(projectTypes);
        this.store.dispatch(clearCreatedNewProject());
      })
      .untilDestroyed(this);
  }

  private handleBoardLayoutTypeChange(layoutType: BoardLayoutType, showPermissionWarning = true): void {
    if (layoutType === BoardLayoutType.ProjectsBoard && !this.canAccessProjectBoards()) {
      if (showPermissionWarning) {
        this.snackbarService.error('Please ask your account manager to add ProjectsList_View permission');
      }
      const fallbackLayoutType = BoardLayoutType.TurnoverBoard;
      this.lastAllowedBoardLayoutType = fallbackLayoutType;
      this.form.get('boardLayoutType')?.setValue(fallbackLayoutType, { emitEvent: false });
      this.applyBoardLayoutTypeChanges(fallbackLayoutType);
      this.cdr.detectChanges();
      return;
    }

    this.lastAllowedBoardLayoutType = layoutType;
    this.applyBoardLayoutTypeChanges(layoutType);
  }

  private canAccessProjectBoards(): boolean {
    return checkPermissionLevel(this.permissionList, PermissionLevelType.ProjectsList_View, 'any');
  }

  private applyBoardLayoutTypeChanges(layoutType: BoardLayoutType): void {
    const projectTypesControl = this.form.get('projectTypes');
    const progressTypeControl = this.form.get('progressType');
    const datasourceControl = this.form.get('datasource');

    if (layoutType === BoardLayoutType.ProjectsBoard) {
      this.suppressCustomExpand = true;
      this.form.get('boardType')?.setValue(BoardType.Custom);
      this.suppressCustomExpand = false;
      projectTypesControl?.setValidators([Validators.required]);
      progressTypeControl?.setValue(null);
      progressTypeControl?.clearValidators();
      datasourceControl?.setValue(BoardSourceType.Manual);
      this.form.controls.name.setValue('Project board');
    } else {
      this.form.get('boardType')?.setValue(BoardType.MakeReady);
      projectTypesControl?.setValue(null);
      projectTypesControl?.clearValidators();
      progressTypeControl?.setValidators([Validators.required]);
      progressTypeControl?.setValue(BoardProgressType.UpdateMakeReadyProgressBar);
      datasourceControl?.setValue(BoardSourceType.MakeReadyUnits);
      this.form.controls.name.setValue('Make Ready');
    }

    projectTypesControl?.updateValueAndValidity();
    progressTypeControl?.updateValueAndValidity();
    this.cdr.markForCheck();
  }

  private updatePropertyList(): void {
    const selectedPortfolioIds = this.form.controls.selectedPortfolioIds.value;

    if (selectedPortfolioIds?.length === this.userPortfolios?.length) {
      this.propertyList = [...this.allProperties];
      return;
    }

    combineLatest([this.store.select(selectPortfolios), this.propertiesService.getAllUserProperties()])
      .pipe(
        take(1),
        map(([portfolios, properties]) => {
          const selectedProperties = properties.filter(property => selectedPortfolioIds.includes(property.portfolioId));

          this.propertyList = selectedProperties.map(item => ({
            value: item.id,
            label: item.name,
          }));

          const currentExcludedIds = this.form.controls.excludedPropertyIds.value || [];
          const availablePropertyIds = this.propertyList.map(p => p.value);
          const newExcludedIds = currentExcludedIds.filter(id => availablePropertyIds.includes(id));

          if (currentExcludedIds?.length !== newExcludedIds?.length) {
            this.form.controls.excludedPropertyIds.setValue(newExcludedIds);
          }

          this.cdr.markForCheck();
        })
      )
      .subscribe()
      .untilDestroyed(this);
  }

  private getDynamicTicketTypes() {
    this.ticketTypeService
      .getDynamicTicketTypes()
      .pipe(take(1))
      .subscribe(res => {
        this.dynamicTicketTypeList = [
          ...DynamicTicketTypeDefault,
          ...res.map(t => ({ value: t.id, label: t.name })).sort((a, b) => a.label.localeCompare(b.label)),
        ];
        this.cdr.markForCheck();
      })
      .untilDestroyed(this);
  }

  toggleAdvancedSettings() {
    this.isSettingsOpened = !this.isSettingsOpened;
  }

  submit() {
    if (this.inDisallowedType()) {
      this.shorError();
      return;
    }
    if (isControlInvalid(this.form)) return this.form.markAllAsTouched();

    this.boardActionSubject.next(true);

    if (this.isEditMode) {
      const allPortfolios = this.form.value.selectedPortfolioIds?.length === this.userPortfolios?.length;
      const updated = {
        ...this.data,
        ...this.form.value,
        selectedPortfolioIds: allPortfolios ? [] : this.form.value.selectedPortfolioIds,
        allPortfolios,
        extendedConfigParsed: this.boardGroupForm.value,
        defaultScheduleDynamicTicketTypeId:
          this.form.value.defaultScheduleDynamicTicketTypeId === DONT_CREATE_TICKETS
            ? null
            : this.form.value.defaultScheduleDynamicTicketTypeId,
        defaultCostDynamicTicketTypeId:
          this.form.value.defaultCostDynamicTicketTypeId === DONT_CREATE_TICKETS
            ? null
            : this.form.value.defaultCostDynamicTicketTypeId,
      };

      this.boardService
        .update(new RestBoardModel(updated))
        .pipe(switchMap(() => this.boardService.getBoardById(this.data.id)))
        .subscribe({
          next: board => {
            this.snackbarService.success(`${updated.name} updated successfully`);
            this.dialogRef.close(updated);
            if (!board?.hasOwnProperty('dataSourceChangesProcessedOn')) {
              this.boardEventService.startPolling(board.id);
            }
          },
          error: err => {
            this.boardActionSubject.next(false);
            console.error(`Failed to update ${updated.name}:`, err);
            const errorMsg = err?.error?.errorCode
              ? err.error.errorCode
              : `Failed to update ${updated.name}. Please try again.`;
            this.snackbarService.error(errorMsg);
          },
        })
        .untilDestroyed(this);

      return;
    }

    const selectedPorfolios: number[] = this.form.get('selectedPortfolioIds')?.value;
    const allPortfolios = selectedPorfolios?.length ? selectedPorfolios.includes(allPortfoliosValue) : false;
    const board = new Board(
      Object.assign({}, this.data, {
        ...this.form.value,
        allPortfolios,
        selectedPortfolioIds: allPortfolios ? [] : selectedPorfolios,
        defaultScheduleDynamicTicketTypeId:
          this.form.value.defaultScheduleDynamicTicketTypeId === DONT_CREATE_TICKETS ||
          !this.form.value.defaultScheduleDynamicTicketTypeId
            ? null
            : this.form.value.defaultScheduleDynamicTicketTypeId,
        defaultCostDynamicTicketTypeId:
          this.form.value.defaultCostDynamicTicketTypeId === DONT_CREATE_TICKETS ||
          !this.form.value.defaultCostDynamicTicketTypeId
            ? null
            : this.form.value.defaultCostDynamicTicketTypeId,
      })
    );

    this.boardService
      .add(new RestBoardModel(board))
      .subscribe({
        next: id => {
          this.router.navigate([AppRoutes.BOARDS, id], {
            relativeTo: this.activatedRoute,
            queryParamsHandling: 'merge',
          });
          this.dialogRef.close();
        },
        error: err => {
          this.boardActionSubject.next(false);
          console.error(`Failed to create board:`, err);
          const errorMsg = err?.error?.errorCode ? err.error.errorCode : `Failed to create board. Please try again.`;
          this.snackbarService.error(errorMsg);
        },
      })
      .untilDestroyed(this);
  }

  close() {
    this.dialogRef.close();
  }

  private initFormGroups(): FormGroup {
    const formGroups = this.formBuilder.group({
      selectedPortfolioIds: [this.data?.selectedPortfolioIds || [], [Validators.required]],
      excludedPropertyIds: [this.data?.excludedPropertyIds || []],
      boardType: [this.data?.boardType, [Validators.required]],
      boardLayoutType: [this.data?.boardLayoutType || BoardLayoutType.TurnoverBoard, [Validators.required]],
      name: [this.data?.name, [Validators.required]],
      progressType: [this.data?.progressType, [Validators.required]],
      datasource: [this.data?.datasource, [Validators.required]],
      projectTypes: [this.data?.projectTypes],
      outputAction: [this.data?.outputAction, []],
      outputBehaviour: [this.data?.outputBehaviour, []],
      showArchivedTurnovers: [this.data?.showArchivedTurnovers || false],
      boardCellColor: [this.data?.boardCellColor ?? '#ffffff'],
      defaultScheduleDynamicTicketTypeId: [
        this.data?.defaultScheduleDynamicTicketTypeId
          ? this.data.defaultScheduleDynamicTicketTypeId
          : DONT_CREATE_TICKETS,
      ],
      defaultCostDynamicTicketTypeId: [
        this.data?.defaultCostDynamicTicketTypeId ? this.data.defaultCostDynamicTicketTypeId : DONT_CREATE_TICKETS,
      ],
      poNumberIsAvailable: [this.data?.poNumberIsAvailable ?? false],
      sendBackBehaviour: [this.data?.sendBackBehaviour ?? SendBackBehaviour.Keep],
      turnTransitBackByTurnFlowBehavior: [
        this.data?.turnTransitBackByTurnFlowBehavior ?? TurnTransitBackByTurnFlowBehavior.Keep,
      ],
    });

    if (formGroups?.get('boardLayoutType')?.value === BoardLayoutType.ProjectsBoard) {
      formGroups.get('progressType').clearValidators();
      formGroups.get('progressType').updateValueAndValidity();
    }

    return formGroups;
  }

  private initBoardGroupConfig(): FormGroup {
    return this.formBuilder.group({
      groups: this.createFormGroupFromGroups(this.data?.extendedConfigParsed?.groups || []),
    });
  }

  private createFormGroupFromGroups(groups: IBoardGroup[]): FormArray {
    return this.formBuilder.array(groups.map(group => this.createGroupFormGroup(group)));
  }

  private createGroupFormGroup(group: IBoardGroup): FormGroup {
    return this.formBuilder.group({
      name: [group.name || 'New group'],
      colorBg: [group.colorBg || '#eeeeee'],
      colorText: [group.colorText || '#212121'],
      cols: [
        intersection(
          group.cols,
          this.colOptions.map(c => c.value)
        ) || [],
      ],
    });
  }

  addNewGroup(): void {
    const groupsArray = this.boardGroupForm.get('groups') as FormArray;
    const colorBg = '#eeeeee';
    const colorText = '#212121';
    groupsArray.push(this.createGroupFormGroup({ name: '', colorBg, colorText, cols: [] }));
  }

  removeGroup(index: number): void {
    const groupsArray = this.boardGroupForm.get('groups') as FormArray;
    groupsArray.removeAt(index);
  }

  private shorError(): void {
    this.snackbarService.error(
      `Turns cannot auto-progress to the same step they started in. Either set "Remove rows" to "manual" or choose an ending destination that is not "${this.enumerationValuePipe.transform(
        this.form.value.outputAction,
        BoardOutputTypeList
      )}"`
    );
  }

  private inDisallowedType(): boolean {
    return (
      this.form.value.outputBehaviour === BoardOutputBehaviour.Automatically &&
      this.form.value.datasource != null &&
      this.form.value.outputAction != null &&
      disallowSourceTypeOutputTypeMap[this.form.value.datasource] === this.form.value.outputAction
    );
  }

  readonly EIcon = EIcon;

  createProject(): void {
    this.dialog
      .open(AddProjectTypeDialogComponent, {
        panelClass: 'modal-container-without-padding',
        width: '380px',
        autoFocus: false,
      })
      .afterClosed()
      .pipe(filterNullish$())
      .subscribe(res => this.onCreateProjectType(res))
      .untilDestroyed(this);
  }

  onCreateProjectType(data: { name: string }): void {
    this.store.dispatch(createProjectType({ name: data.name }));
  }

  onOutputChange(e: MatCheckboxChange): void {
    this.form.get('outputAction').setValue(e.checked ? BoardOutputType.SendToArchive : BoardOutputType.None);
  }
}
